/**
 * ServerClock: estimates the server's millisecond clock from ping/pong
 * (DESIGN 5.6 "Clock sync"). Every pong gives one sample of the offset
 * `serverMs - localMs`; samples are smoothed with an EMA (alpha
 * CLIENT.clockEmaAlpha) so a single delayed pong does not jerk render time.
 *
 * All methods take the local time explicitly (performance.now() ms in the
 * browser) so the class is deterministic and testable without timers. The
 * Socket sends `ping` frames every NET.pingIntervalMs with `c` = its local
 * send time; the pong echoes `c`, which is how rtt is measured.
 */

import type { ClientMsg } from '@shared/index.js';
import { CLIENT, NET } from '@shared/index.js';

export const PING_INTERVAL_MS = NET.pingIntervalMs;

export class ServerClock {
  /** Smoothed serverMs - localMs; meaningless until `synced`. */
  private _offset = 0;
  private _rtt = 0;
  private _synced = false;
  private _samples = 0;
  private _lastPingAt = Number.NEGATIVE_INFINITY;

  constructor(private readonly alpha: number = CLIENT.clockEmaAlpha) {}

  /** Smoothed round trip in ms (0 before the first pong). */
  get rtt(): number { return this._rtt; }
  /** Smoothed serverMs - localMs. */
  get offset(): number { return this._offset; }
  /** True once at least one pong (or a seed) has been applied. */
  get synced(): boolean { return this._synced; }
  /** Pongs applied so far. */
  get samples(): number { return this._samples; }

  /** Estimated server ms for the local instant `localNow`. */
  now(localNow: number): number {
    return localNow + this._offset;
  }

  /** True when a ping is due (PING_INTERVAL_MS since the last one). */
  pingDue(localNow: number): boolean {
    return localNow - this._lastPingAt >= PING_INTERVAL_MS;
  }

  /** Build the ping frame for `localNow` and remember when it went out. */
  makePing(localNow: number): Extract<ClientMsg, { t: 'ping' }> {
    this._lastPingAt = localNow;
    return { t: 'ping', c: localNow };
  }

  /**
   * Apply one pong. `c` is the local send time echoed back, `st` the server
   * ms when it answered, `localNow` the local receive time. The sample's
   * server time is st + rtt/2 (DESIGN 5.6). The first sample is taken as-is;
   * later ones blend in with `alpha`. Pongs with c in the future (clock
   * discontinuity) are ignored.
   */
  onPong(c: number, st: number, localNow: number): void {
    const rtt = localNow - c;
    if (!Number.isFinite(rtt) || rtt < 0 || !Number.isFinite(st)) return;
    const sampleOffset = st + rtt / 2 - localNow;
    if (this._samples === 0) {
      // First real sample replaces whatever a snapshot seed guessed.
      this._offset = sampleOffset;
      this._rtt = rtt;
      this._synced = true;
    } else {
      this._offset += this.alpha * (sampleOffset - this._offset);
      this._rtt += this.alpha * (rtt - this._rtt);
    }
    this._samples++;
  }

  /**
   * Seed the offset from a snapshot's `st` before the first pong arrives, so
   * the first two seconds are not rendered against a zero clock. Ignores
   * one-way latency (about rtt/2 behind); the first pong replaces it.
   */
  seed(st: number, localNow: number): void {
    if (this._synced) return;
    this._offset = st - localNow;
    this._synced = true;
  }

  /** Forget everything (new connection / new server). */
  reset(): void {
    this._offset = 0;
    this._rtt = 0;
    this._synced = false;
    this._samples = 0;
    this._lastPingAt = Number.NEGATIVE_INFINITY;
  }
}
